import React, { useCallback, useEffect, useRef, useState } from "react";

import { Box, InputAdornment } from "@material-ui/core";

import { useNotify, useVersion } from "react-admin";
import { useForm } from "react-final-form";

import { logError } from "../../../utils/appUtils";
import { resolveScheduledDateTime } from "../../../utils/dateUtils";
import { resolveDefaultEventType } from "../../../managerApp/resources/eventTypes/EventTypeUtils";
import { YardDateTimeInput } from "../YardDateTimeInput";
import { CreatedAtIcon } from "../iconography";
import { Scheduler } from "./Scheduler";

/**
 * Resolve the event type a form should schedule against, plus the timeslot
 * state the save path needs.
 *
 * An existing booking keeps its own event type; otherwise fall back to the
 * account default (and then to the yard's list) via resolveDefaultEventType.
 * `canSchedule` is false when the yard has no event type to book at all.
 */
export const useScheduleEventType = (record, workflow, defaultEventTypeId) => {
  const notify = useNotify();
  const version = useVersion();
  const [eventType, setEventType] = useState(record?.timeslot?.eventType);
  const [canSchedule, setCanSchedule] = useState(
    Boolean(record?.timeslot?.eventType),
  );
  const [selectedTimeslot, setSelectedTimeslot] = useState(record?.timeslot);
  // The record object identity changes on any store refresh; once the user has
  // picked a slot, re-seeding from the record would drop the unsaved pick and
  // the save would move scheduledDateTime without booking an appointment.
  const pickedRef = useRef(false);
  // An explicit refresh is the exception, and the version is what tells the two
  // apart: incidental store churn leaves it alone, useRefresh and the app bar's
  // refresh button increment it. Asking for the server's answer has to drop the
  // pick — react-admin keys the form on the same version, so the field would
  // otherwise reinitialize from the refetched record while selectedTimeslot,
  // which is what the save actually books, still held the rejected slot.
  const seededVersionRef = useRef(version);
  const selectTimeslot = useCallback((timeslot) => {
    pickedRef.current = true;
    setSelectedTimeslot(timeslot);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (version !== seededVersionRef.current) {
      seededVersionRef.current = version;
      pickedRef.current = false;
    }
    if (pickedRef.current) return undefined;
    // Seeded either way, not just from an existing booking: a refresh that drops
    // a pick on a shipment that has none must come back to none, rather than
    // keep the slot the server just rejected.
    setSelectedTimeslot(record?.timeslot);
    if (record?.timeslot) {
      // Editing an existing booking: keep its event type.
      setEventType(record.timeslot.eventType);
      setCanSchedule(Boolean(record.timeslot.eventType));
    } else if (record) {
      resolveDefaultEventType(defaultEventTypeId, workflow)
        .then(({ eventType: resolved, canSchedule: allowed }) => {
          if (cancelled) return;
          setEventType(resolved);
          setCanSchedule(allowed);
        })
        .catch((err) =>
          logError("scheduler:useScheduleEventType", err, notify),
        );
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, workflow, defaultEventTypeId, version]);

  return {
    eventType,
    canSchedule,
    selectedTimeslot,
    setSelectedTimeslot: selectTimeslot,
  };
};

/**
 * DateTimeInput whose only editor is the Scheduler: the text field is
 * read-only, and the picked timeslot is written back to both `source` and
 * `setSelectedTimeslot` so the save path can book the appointment.
 *
 * By default the calendar is a dialog the field opens on click. With `inline`
 * the calendar is rendered under the field instead and every pick commits on
 * the spot — for forms whose whole purpose is scheduling, where a dialog is a
 * click and a modal in the way of the one thing the page is for.
 */
export const ScheduleDateTimeInput = ({
  source = "scheduledDateTime",
  label,
  account,
  workflow,
  eventType,
  canSchedule,
  selectedTimeslot,
  setSelectedTimeslot,
  helperText = false,
  inline = false,
  ...props
}) => {
  const form = useForm();
  const notify = useNotify();
  const [isSchedulerOpen, setIsSchedulerOpen] = useState(false);

  const handleSchedulerOpen = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (canSchedule) setIsSchedulerOpen(true);
  };

  const ignoreEvent = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleTimeslot = (timeslot) => {
    if (!timeslot) {
      // Cleared: the field has to go with it, or the form would still save the
      // time the scheduler no longer shows as picked.
      form.change(source, null);
      setSelectedTimeslot(null);
      return;
    }
    const scheduledDate = resolveScheduledDateTime(timeslot, {
      notify,
      scope: "scheduler:ScheduleDateTimeInput:handleTimeslot",
    });
    if (!scheduledDate) return;
    form.change(source, scheduledDate);
    setSelectedTimeslot(timeslot);
  };

  // Inline still keeps the field: it is what holds the form value the save
  // reads, what `required` validates, and the one place the committed pick is
  // spelled out in full — the calendar only ever shows the day being browsed.
  const field = (
    <YardDateTimeInput
      {...props}
      source={source}
      label={label}
      variant="outlined"
      fullWidth
      disabled={!canSchedule}
      onClick={inline ? undefined : handleSchedulerOpen}
      onKeyPress={ignoreEvent}
      options={{
        readOnly: true,
        InputProps: {
          readOnly: true,
          endAdornment: inline ? undefined : (
            <InputAdornment position="end">
              <CreatedAtIcon onClick={handleSchedulerOpen} />
            </InputAdornment>
          ),
        },
      }}
      helperText={helperText}
    />
  );

  if (inline)
    return (
      <Box>
        {field}
        {canSchedule && (
          <Box mt={1}>
            <Scheduler
              inline
              eventType={eventType}
              workflow={workflow}
              account={account}
              resettable={false}
              selectedTimeslot={selectedTimeslot}
              setSelectedTimeslot={handleTimeslot}
            />
          </Box>
        )}
      </Box>
    );

  return (
    <>
      {field}
      {canSchedule && (
        <Scheduler
          eventType={eventType}
          workflow={workflow}
          account={account}
          open={isSchedulerOpen}
          setIsOpen={setIsSchedulerOpen}
          resettable={false}
          selectedTimeslot={selectedTimeslot}
          setSelectedTimeslot={handleTimeslot}
        />
      )}
    </>
  );
};
